import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertCircleIcon,
  CheckCircle2Icon,
  Clock3Icon,
  HelpCircleIcon,
  Loader2Icon,
  MessageSquareIcon,
  PaperclipIcon,
  PlusIcon,
  SearchIcon,
  SendIcon,
  TicketIcon,
  XCircleIcon,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { ResponsivePageHeader } from '@nebula/shared/components'
import {
  supportApi,
  type SupportTicketCategory,
  type SupportTicketDetail,
  type SupportTicketPriority,
  type SupportTicketStatus,
  type SupportTicketSummary,
} from '@nebula/shared/services'
import { useAuthStore } from '@nebula/shared/store'

const STATUS_LABEL: Record<string, string> = {
  open: 'Aberto',
  in_progress: 'Em andamento',
  waiting_customer: 'Aguardando cliente',
  resolved: 'Resolvido',
  closed: 'Fechado',
}

const PRIORITY_LABEL: Record<string, string> = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
  critical: 'Crítica',
}

const CATEGORY_LABEL: Record<string, string> = {
  technical: 'Técnico',
  billing: 'Cobrança',
  network: 'Rede',
  security: 'Segurança',
  general: 'Geral',
  other: 'Outro',
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'open':
      return 'bg-blue-100 text-blue-700'
    case 'in_progress':
      return 'bg-amber-100 text-amber-700'
    case 'waiting_customer':
      return 'bg-purple-100 text-purple-700'
    case 'resolved':
      return 'bg-green-100 text-green-700'
    case 'closed':
      return 'bg-gray-100 text-gray-700'
    default:
      return 'bg-gray-100 text-gray-700'
  }
}

function priorityBadgeClass(priority: string): string {
  switch (priority) {
    case 'low':
      return 'bg-emerald-100 text-emerald-700'
    case 'medium':
      return 'bg-blue-100 text-blue-700'
    case 'high':
      return 'bg-orange-100 text-orange-700'
    case 'critical':
      return 'bg-red-100 text-red-700'
    default:
      return 'bg-gray-100 text-gray-700'
  }
}

function statusIcon(status: string) {
  switch (status) {
    case 'open':
      return <TicketIcon className="size-4" />
    case 'in_progress':
      return <Clock3Icon className="size-4" />
    case 'waiting_customer':
      return <MessageSquareIcon className="size-4" />
    case 'resolved':
      return <CheckCircle2Icon className="size-4" />
    case 'closed':
      return <XCircleIcon className="size-4" />
    default:
      return <AlertCircleIcon className="size-4" />
  }
}

function formatDate(date: string | null | undefined): string {
  if (!date) return '-'

  const parsed = new Date(date)
  return parsed.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const SupportPage = () => {
  const queryClient = useQueryClient()
  const { user } = useAuthStore()

  const isCaptive = Boolean(user?.organization?.parent_id)

  const [queue, setQueue] = useState<'open' | 'resolved' | 'closed' | 'all'>('open')
  const [status, setStatus] = useState<SupportTicketStatus | ''>('')
  const [search, setSearch] = useState('')
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null)

  const [newTicketOpen, setNewTicketOpen] = useState(false)
  const [newTicketSubject, setNewTicketSubject] = useState('')
  const [newTicketDescription, setNewTicketDescription] = useState('')
  const [newTicketCategory, setNewTicketCategory] = useState<SupportTicketCategory>('technical')
  const [newTicketPriority, setNewTicketPriority] = useState<SupportTicketPriority>('medium')
  const [newTicketResellerId, setNewTicketResellerId] = useState('')
  const [newTicketVmId, setNewTicketVmId] = useState('')
  const [newTicketFiles, setNewTicketFiles] = useState<File[]>([])

  const [replyBody, setReplyBody] = useState('')
  const [replyFiles, setReplyFiles] = useState<File[]>([])

  const { data: ticketList, isLoading: listLoading } = useQuery({
    queryKey: ['ec-support-tickets', queue, status, search],
    queryFn: () =>
      supportApi.ec.listTickets({
        queue,
        status: status || undefined,
        search: search || undefined,
        limit: 100,
      }),
  })

  const { data: ticketDetail, isLoading: detailLoading } = useQuery<SupportTicketDetail>({
    queryKey: ['ec-support-ticket-detail', selectedTicketId],
    queryFn: () => supportApi.ec.getTicket(selectedTicketId as string),
    enabled: Boolean(selectedTicketId),
  })

  const createTicketMutation = useMutation({
    mutationFn: supportApi.ec.createTicket,
    onSuccess: (ticket) => {
      toast.success('Ticket criado com sucesso')
      setNewTicketOpen(false)
      setNewTicketSubject('')
      setNewTicketDescription('')
      setNewTicketCategory('technical')
      setNewTicketPriority('medium')
      setNewTicketResellerId('')
      setNewTicketVmId('')
      setNewTicketFiles([])
      setSelectedTicketId(ticket.id)
      queryClient.invalidateQueries({ queryKey: ['ec-support-tickets'] })
      queryClient.setQueryData(['ec-support-ticket-detail', ticket.id], ticket)
    },
    onError: (error: any) => {
      const message = error?.response?.data?.detail || 'Falha ao criar ticket'
      toast.error(message)
    },
  })

  const sendMessageMutation = useMutation({
    mutationFn: ({ ticketId, body, files }: { ticketId: string; body: string; files: File[] }) =>
      supportApi.ec.addMessage(ticketId, { body, attachments: files }),
    onSuccess: (ticket) => {
      toast.success('Mensagem enviada')
      setReplyBody('')
      setReplyFiles([])
      queryClient.invalidateQueries({ queryKey: ['ec-support-tickets'] })
      queryClient.setQueryData(['ec-support-ticket-detail', ticket.id], ticket)
    },
    onError: (error: any) => {
      const message = error?.response?.data?.detail || 'Falha ao enviar mensagem'
      toast.error(message)
    },
  })

  const closeTicketMutation = useMutation({
    mutationFn: ({ ticketId, note }: { ticketId: string; note?: string }) =>
      supportApi.ec.closeTicket(ticketId, note),
    onSuccess: (ticket) => {
      toast.success('Ticket fechado')
      queryClient.invalidateQueries({ queryKey: ['ec-support-tickets'] })
      queryClient.setQueryData(['ec-support-ticket-detail', ticket.id], ticket)
    },
    onError: (error: any) => {
      const message = error?.response?.data?.detail || 'Falha ao fechar ticket'
      toast.error(message)
    },
  })

  useEffect(() => {
    if (!selectedTicketId && ticketList?.tickets?.length) {
      setSelectedTicketId(ticketList.tickets[0].id)
    }

    if (
      selectedTicketId &&
      ticketList &&
      !ticketList.tickets.some((ticket) => ticket.id === selectedTicketId)
    ) {
      setSelectedTicketId(ticketList.tickets[0]?.id || null)
    }
  }, [ticketList, selectedTicketId])

  const selectedSummary = useMemo<SupportTicketSummary | undefined>(
    () => ticketList?.tickets.find((ticket) => ticket.id === selectedTicketId),
    [ticketList, selectedTicketId],
  )

  const handleCreateTicket = () => {
    if (!newTicketSubject.trim() || !newTicketDescription.trim()) {
      toast.error('Preencha assunto e descrição')
      return
    }

    createTicketMutation.mutate({
      subject: newTicketSubject.trim(),
      description: newTicketDescription.trim(),
      category: newTicketCategory,
      priority: newTicketPriority,
      reseller_id: newTicketResellerId.trim() || undefined,
      vm_instance_id: newTicketVmId.trim() || undefined,
      attachments: newTicketFiles,
    })
  }

  const handleSendMessage = () => {
    if (!selectedTicketId) return
    if (!replyBody.trim() && replyFiles.length === 0) {
      toast.error('Digite uma mensagem ou anexe um arquivo')
      return
    }

    sendMessageMutation.mutate({
      ticketId: selectedTicketId,
      body: replyBody,
      files: replyFiles,
    })
  }

  return (
    <div className="mobile-page-padding space-y-6">
      <ResponsivePageHeader
        title="Suporte"
        description="Abra chamados de suporte e acompanhe o andamento do seu atendimento."
        icon={HelpCircleIcon}
        actions={
          <button
            onClick={() => setNewTicketOpen((value) => !value)}
            className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700"
          >
            <PlusIcon className="size-4" />
            Novo Ticket
          </button>
        }
      />

      {newTicketOpen && (
        <section className="rounded-xl border border-purple-100 bg-purple-50/50 p-4">
          <h2 className="mb-3 text-sm font-semibold text-gray-900">Criar Ticket</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <input
              value={newTicketSubject}
              onChange={(event) => setNewTicketSubject(event.target.value)}
              placeholder="Assunto"
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            <select
              value={newTicketCategory}
              onChange={(event) =>
                setNewTicketCategory(event.target.value as SupportTicketCategory)
              }
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="technical">Técnico</option>
              <option value="billing">Cobrança</option>
              <option value="network">Rede</option>
              <option value="security">Segurança</option>
              <option value="general">Geral</option>
              <option value="other">Outro</option>
            </select>
            <select
              value={newTicketPriority}
              onChange={(event) =>
                setNewTicketPriority(event.target.value as SupportTicketPriority)
              }
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="low">Baixa</option>
              <option value="medium">Média</option>
              <option value="high">Alta</option>
              <option value="critical">Crítica</option>
            </select>
            <input
              value={newTicketVmId}
              onChange={(event) => setNewTicketVmId(event.target.value)}
              placeholder="VM ID (opcional)"
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            {!isCaptive && (
              <input
                value={newTicketResellerId}
                onChange={(event) => setNewTicketResellerId(event.target.value)}
                placeholder="Reseller ID (obrigatório para cliente livre)"
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm md:col-span-2"
              />
            )}
            <textarea
              value={newTicketDescription}
              onChange={(event) => setNewTicketDescription(event.target.value)}
              placeholder="Descreva o problema em detalhes"
              rows={4}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm md:col-span-2"
            />
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-gray-300 bg-white px-3 py-2 text-sm text-gray-600 md:col-span-2">
              <PaperclipIcon className="size-4" />
              <span>
                {newTicketFiles.length
                  ? `${newTicketFiles.length} arquivo(s) anexado(s)`
                  : 'Anexar arquivos'}
              </span>
              <input
                type="file"
                multiple
                className="hidden"
                onChange={(event) => setNewTicketFiles(Array.from(event.target.files || []))}
              />
            </label>
          </div>

          <div className="mt-3 flex justify-end gap-2">
            <button
              onClick={() => setNewTicketOpen(false)}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700"
            >
              Cancelar
            </button>
            <button
              onClick={handleCreateTicket}
              disabled={createTicketMutation.isPending}
              className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-60"
            >
              {createTicketMutation.isPending ? (
                <Loader2Icon className="size-4 animate-spin" />
              ) : (
                <PlusIcon className="size-4" />
              )}
              Criar
            </button>
          </div>
        </section>
      )}

      <section className="grid grid-cols-1 gap-4 xl:grid-cols-[360px_minmax(0,1fr)]">
        <div className="rounded-xl border border-gray-200 bg-white">
          <div className="border-b border-gray-100 p-3">
            <div className="flex flex-wrap gap-2">
              {(['open', 'resolved', 'closed', 'all'] as const).map((value) => (
                <button
                  key={value}
                  onClick={() => setQueue(value)}
                  className={`rounded-full px-3 py-1 text-xs font-medium ${queue === value ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                >
                  {value === 'open'
                    ? 'Abertos'
                    : value === 'resolved'
                      ? 'Resolvidos'
                      : value === 'closed'
                        ? 'Fechados'
                        : 'Todos'}
                </button>
              ))}
            </div>
            <div className="mt-2 flex items-center gap-2 rounded-lg border border-gray-200 px-2 py-1.5">
              <SearchIcon className="size-4 text-gray-400" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar ticket"
                className="w-full border-0 bg-transparent p-0 text-sm outline-none"
              />
            </div>
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value as SupportTicketStatus | '')}
              className="mt-2 w-full rounded-lg border border-gray-200 p-2 text-sm"
            >
              <option value="">Todos os status</option>
              <option value="open">Aberto</option>
              <option value="in_progress">Em andamento</option>
              <option value="waiting_customer">Aguardando cliente</option>
              <option value="resolved">Resolvido</option>
              <option value="closed">Fechado</option>
            </select>
          </div>

          <div className="max-h-[700px] overflow-y-auto">
            {listLoading && (
              <div className="flex items-center justify-center p-8">
                <Loader2Icon className="size-5 animate-spin text-purple-600" />
              </div>
            )}

            {!listLoading && ticketList?.tickets.length === 0 && (
              <div className="p-6 text-center text-sm text-gray-500">Nenhum ticket encontrado</div>
            )}

            {ticketList?.tickets.map((ticket) => (
              <button
                key={ticket.id}
                onClick={() => setSelectedTicketId(ticket.id)}
                className={`w-full border-b border-gray-100 p-3 text-left transition hover:bg-gray-50 ${selectedTicketId === ticket.id ? 'bg-purple-50' : ''}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-xs font-semibold text-gray-500">{ticket.ticket_number}</p>
                    <p className="line-clamp-2 text-sm font-medium text-gray-900">
                      {ticket.subject}
                    </p>
                  </div>
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${statusBadgeClass(ticket.status)}`}
                  >
                    {statusIcon(ticket.status)}
                    {STATUS_LABEL[ticket.status] || ticket.status}
                  </span>
                </div>
                <p className="mt-2 text-xs text-gray-600">{ticket.description_preview}</p>
                <div className="mt-2 flex items-center justify-between text-[11px] text-gray-500">
                  <span
                    className={`rounded-full px-2 py-0.5 ${priorityBadgeClass(ticket.priority)}`}
                  >
                    {PRIORITY_LABEL[ticket.priority] || ticket.priority}
                  </span>
                  <span>{formatDate(ticket.updated_at)}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white">
          {!selectedTicketId && (
            <div className="p-8 text-center text-sm text-gray-500">
              Selecione um ticket para visualizar detalhes
            </div>
          )}

          {selectedTicketId && detailLoading && (
            <div className="flex items-center justify-center p-8">
              <Loader2Icon className="size-5 animate-spin text-purple-600" />
            </div>
          )}

          {selectedTicketId && ticketDetail && (
            <>
              <div className="border-b border-gray-100 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold text-gray-500">
                      {ticketDetail.ticket_number}
                    </p>
                    <h2 className="text-lg font-semibold text-gray-900">{ticketDetail.subject}</h2>
                    <p className="mt-1 text-sm text-gray-600">{ticketDetail.description}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-medium ${statusBadgeClass(ticketDetail.status)}`}
                    >
                      {STATUS_LABEL[ticketDetail.status] || ticketDetail.status}
                    </span>
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-medium ${priorityBadgeClass(ticketDetail.priority)}`}
                    >
                      {PRIORITY_LABEL[ticketDetail.priority] || ticketDetail.priority}
                    </span>
                    <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                      {CATEGORY_LABEL[ticketDetail.category] || ticketDetail.category}
                    </span>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  {ticketDetail.status !== 'closed' && (
                    <button
                      onClick={() => closeTicketMutation.mutate({ ticketId: ticketDetail.id })}
                      disabled={closeTicketMutation.isPending}
                      className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Fechar ticket
                    </button>
                  )}
                </div>
              </div>

              <div className="max-h-[430px] space-y-3 overflow-y-auto p-4">
                {ticketDetail.messages.map((message) => {
                  const fromCustomer = message.author_org_id === ticketDetail.origin_org_id

                  return (
                    <div
                      key={message.id}
                      className={`rounded-lg border p-3 ${fromCustomer ? 'border-purple-200 bg-purple-50/40' : 'border-blue-200 bg-blue-50/40'}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs font-semibold text-gray-700">
                          {message.author_name || 'Sistema'}
                          {message.author_org_name ? ` • ${message.author_org_name}` : ''}
                        </p>
                        <p className="text-[11px] text-gray-500">
                          {formatDate(message.created_at)}
                        </p>
                      </div>
                      <p className="mt-1 text-sm whitespace-pre-wrap text-gray-800">
                        {message.body}
                      </p>

                      {message.attachments.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {message.attachments.map((attachment) => (
                            <a
                              key={attachment.id}
                              href={attachment.public_url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                            >
                              <PaperclipIcon className="size-3.5" />
                              {attachment.file_name}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>

              {ticketDetail.status !== 'closed' && (
                <div className="border-t border-gray-100 p-4">
                  <textarea
                    value={replyBody}
                    onChange={(event) => setReplyBody(event.target.value)}
                    rows={3}
                    placeholder="Digite sua resposta"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />

                  <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-xs text-gray-600">
                      <PaperclipIcon className="size-4" />
                      {replyFiles.length ? `${replyFiles.length} anexo(s)` : 'Anexar arquivos'}
                      <input
                        type="file"
                        multiple
                        className="hidden"
                        onChange={(event) => setReplyFiles(Array.from(event.target.files || []))}
                      />
                    </label>

                    <button
                      onClick={handleSendMessage}
                      disabled={sendMessageMutation.isPending}
                      className="inline-flex items-center justify-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-60"
                    >
                      {sendMessageMutation.isPending ? (
                        <Loader2Icon className="size-4 animate-spin" />
                      ) : (
                        <SendIcon className="size-4" />
                      )}
                      Enviar
                    </button>
                  </div>
                </div>
              )}

              {selectedSummary && (
                <div className="border-t border-gray-100 p-4 text-xs text-gray-500">
                  Atualizado em {formatDate(selectedSummary.updated_at)}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  )
}

export default SupportPage
